import { useMemo, useRef, useState } from "react";

import { CloseIcon, DefaultImageIcon, TrashIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import SearchBox from "@/components/ui/searchBox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useOnClickOutside from "@/hooks/ui/useClickOutside";
import { cn } from "@/lib/utils";
import { MarkerImage, MarkerTab } from "@/types/dataset";
import {
  addRecentMarkerIcon,
  addRecentMarkerImage,
  ALL_LEGEND_ICONS,
  getLegendIconLabel,
  getRecentMarkerIcons,
  getRecentMarkerImages,
  LEGEND_ICON_CATEGORIES,
  RecentMarkerImage
} from "@/utils/mapLegend";

import { Icon } from "equator-design-system";
import { twMerge } from "tailwind-merge";

// SVG, PNG, JPG and max 5kb
const ACCEPTED_IMAGE_TYPES =
  "image/svg+xml,image/png,image/jpeg,.svg,.png,.jpg,.jpeg";
const ACCEPTED_MIME_TYPES = new Set([
  "image/svg+xml",
  "image/png",
  "image/jpeg"
]);
const ACCEPTED_EXTENSION = /\.(svg|png|jpe?g)$/i;
const MAX_IMAGE_SIZE_BYTES = 5 * 1024; // 5KB

interface MarkerVisualPanelProps {
  initialImage?: MarkerImage | null;
  initialIcon?: string | null;
  onApply: (image: MarkerImage) => void;
  onApplyIcon?: (iconId: string) => void;
  onRemove?: () => void;
  onClose: () => void;
}

/** A selectable icon cell shared by the Recent row and the category grid. */
const IconGridButton = ({
  iconId,
  selected,
  onSelect,
  testId
}: {
  iconId: string;
  selected: boolean;
  onSelect: (iconId: string) => void;
  testId: string;
}) => (
  <button
    type="button"
    onClick={() => onSelect(iconId)}
    aria-label={`Icon ${getLegendIconLabel(iconId)}`}
    title={getLegendIconLabel(iconId)}
    data-testid={testId}
    className={cn(
      "flex size-11 shrink-0 items-center justify-center rounded-lg border border-gainsboro",
      "hover:border-primary-teal",
      selected && "border-primary-teal border-2"
    )}
  >
    <Icon
      name={iconId}
      width={22}
      height={22}
      className={selected ? "text-primary-teal" : "text-txtBody"}
    />
  </button>
);

const MarkerVisualPanel = ({
  initialImage = null,
  initialIcon = null,
  onApply,
  onApplyIcon,
  onRemove,
  onClose
}: MarkerVisualPanelProps) => {
  const panelRef = useRef(null);
  useOnClickOutside(panelRef, onClose, []);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<MarkerTab>(initialIcon ? "icon" : "upload");
  const [recentImages] = useState<RecentMarkerImage[]>(() =>
    getRecentMarkerImages()
  );
  const [recentIcons] = useState<string[]>(() => getRecentMarkerIcons());
  const [selectedImage, setSelectedImage] = useState<MarkerImage | null>(
    initialImage
  );
  const [error, setError] = useState<string | null>(null);

  const [selectedIcon, setSelectedIcon] = useState<string | null>(initialIcon);
  const [iconSearch, setIconSearch] = useState("");
  const [iconCategory, setIconCategory] = useState<string>("all");

  // Icons of the active category chip, narrowed by the search text (matched
  // against the id and the display label, case-insensitive).
  const visibleIcons = useMemo(() => {
    const categoryIcons =
      iconCategory === "all"
        ? ALL_LEGEND_ICONS
        : (LEGEND_ICON_CATEGORIES.find((c) => c.id === iconCategory)?.icons ??
          []);
    const query = iconSearch.trim().toLowerCase();
    if (!query) return categoryIcons;
    return categoryIcons.filter(
      (iconId) =>
        iconId.includes(query) ||
        getLegendIconLabel(iconId).toLowerCase().includes(query)
    );
  }, [iconCategory, iconSearch]);

  const handleSelectFile = (file?: File) => {
    if (!file) return;
    const isSupported =
      ACCEPTED_MIME_TYPES.has(file.type) || ACCEPTED_EXTENSION.test(file.name);
    if (!isSupported) {
      setError("Only SVG, PNG, and JPG images are supported.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError("Image must be 5KB or smaller.");
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () =>
      setSelectedImage({
        url: reader.result as string,
        name: file.name,
        assetId: null
      });
    reader.readAsDataURL(file);
  };

  const handleApply = () => {
    if (!selectedImage) return;
    // Cache in the browser's recent list so it can be re-applied without
    // re-picking; the actual server upload happens on project save.
    addRecentMarkerImage(selectedImage);
    onApply(selectedImage);
    onClose();
  };

  const handleApplyIcon = () => {
    if (!selectedIcon) return;
    addRecentMarkerIcon(selectedIcon);
    onApplyIcon?.(selectedIcon);
    onClose();
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setError(null);
    onRemove?.();
  };

  return (
    <div
      ref={panelRef}
      data-testid="marker-visual-panel"
      className={twMerge(
        "z-[99] font-sans bg-white rounded-lg w-[338px]",
        "flex flex-col py-[16px] fixed left-[430px] top-[30px] text-xs"
      )}
    >
      {/* Header */}
      <div className="flex flex-row items-start justify-between px-4 border-b border-gainsboro pb-2">
        <h2 className="text-txtHeading text-[20px] font-bold">Marker Visual</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close marker visual"
          data-testid="marker-visual-close"
          className="border-0 bg-transparent p-0"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>

      <div className="mt-3 flex flex-col gap-3 px-4">
        <Tabs value={tab} onValueChange={(v) => setTab(v as MarkerTab)}>
          <TabsList className="grid w-full grid-cols-2 h-12">
            <TabsTrigger
              value="upload"
              className="text-[14px] data-[state=active]:text-primary-teal data-[state=active]:font-semibold data-[state=active]:rounded-lg h-10"
            >
              Upload Image
            </TabsTrigger>
            <TabsTrigger
              value="icon"
              className="text-[14px] data-[state=active]:text-primary-teal data-[state=active]:font-semibold data-[state=active]:rounded-lg h-10"
              data-testid="marker-visual-icon-tab"
            >
              Select Icon
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "upload" ? (
          <>
            {/* Recent images (max 5) */}
            {recentImages.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-grey">
                  Recent
                </span>
                <div className="flex gap-2" data-testid="marker-visual-recent">
                  {recentImages.map((image) => (
                    <button
                      key={image.assetId ?? image.url}
                      type="button"
                      onClick={() => setSelectedImage(image)}
                      aria-label={`Recent image ${image.name}`}
                      data-testid="marker-visual-recent-item"
                      className={cn(
                        "size-11 shrink-0 overflow-hidden rounded-lg border border-gainsboro",
                        selectedImage &&
                          (selectedImage.assetId ?? selectedImage.url) ===
                            (image.assetId ?? image.url) &&
                          "ring-2 ring-primary-teal ring-offset-1"
                      )}
                      style={{
                        backgroundImage: `url(${image.url})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center"
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Preview / dropzone */}
            {selectedImage ? (
              <div
                className="relative flex items-center justify-center rounded-lg border border-dashed border-gainsboro px-4 py-8"
                data-testid="marker-visual-preview"
              >
                <img
                  src={selectedImage.url}
                  alt={selectedImage.name}
                  className="size-16 rounded-xl object-cover"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  aria-label="Remove image"
                  data-testid="marker-visual-remove"
                  className="absolute bottom-2 right-2"
                >
                  <TrashIcon className="size-4 fill-cinnabar" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleSelectFile(e.dataTransfer.files?.[0]);
                }}
                className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gainsboro px-4 py-8 text-center hover:border-primary-teal"
                data-testid="marker-visual-dropzone"
              >
                <DefaultImageIcon
                  className="size-8 text-grey"
                  title="default-image"
                />
                <span className="text-xs text-txtHeading">
                  Drag &amp; drop file or{" "}
                  <span className="text-blue-500">click to upload</span>
                </span>
              </button>
            )}

            <p className="text-[10px] text-grey">
              Only support .svg, .png and .jpg. Recommendation resolution
              150&times;150 and max size 5KB
            </p>
            {error && (
              <p
                className="text-[10px] text-cinnabar"
                data-testid="marker-visual-error"
              >
                {error}
              </p>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              className="hidden"
              onChange={(e) => {
                handleSelectFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />

            <Button
              type="button"
              size="sm"
              disabled={!selectedImage}
              onClick={handleApply}
              className="h-8 rounded-lg text-xs"
              data-testid="marker-visual-apply"
            >
              Upload and Apply
            </Button>
          </>
        ) : (
          <>
            {/* Icon search */}
            <SearchBox
              value={iconSearch}
              onChange={(e) => setIconSearch(e.currentTarget.value)}
              placeHolder="Search icon"
            />

            {/* Category chips ("All" + the design-system categories) */}
            <div
              className="no-scrollbar flex gap-1.5 overflow-x-auto pb-1"
              data-testid="marker-visual-icon-categories"
            >
              {[{ id: "all", label: "All" }, ...LEGEND_ICON_CATEGORIES].map(
                (category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setIconCategory(category.id)}
                    data-testid={`marker-visual-icon-category-${category.id}`}
                    className={cn(
                      "shrink-0 rounded-lg border px-2.5 py-1 text-[12px]",
                      iconCategory === category.id
                        ? "border-primary-teal bg-primary-teal/10 font-semibold text-primary-teal"
                        : "border-gainsboro text-txtBody hover:border-primary-teal"
                    )}
                  >
                    {category.label}
                  </button>
                )
              )}
            </div>

            {/* Recent icons (max 5) */}
            {recentIcons.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-[14px] font-semibold text-txtHeading">
                  Recent
                </span>
                <div
                  className="flex gap-2"
                  data-testid="marker-visual-recent-icons"
                >
                  {recentIcons.map((iconId) => (
                    <IconGridButton
                      key={iconId}
                      iconId={iconId}
                      selected={selectedIcon === iconId}
                      onSelect={setSelectedIcon}
                      testId="marker-visual-recent-icon-item"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Icon grid */}
            <div className="flex flex-col gap-1">
              <span className="text-[14px] font-semibold text-txtHeading">
                Icons
              </span>
              {visibleIcons.length > 0 ? (
                <div
                  className="grid max-h-56 grid-cols-5 gap-2 overflow-y-auto no-scrollbar"
                  data-testid="marker-visual-icon-grid"
                >
                  {visibleIcons.map((iconId) => (
                    <IconGridButton
                      key={iconId}
                      iconId={iconId}
                      selected={selectedIcon === iconId}
                      onSelect={setSelectedIcon}
                      testId={`marker-visual-icon-${iconId}`}
                    />
                  ))}
                </div>
              ) : (
                <div
                  className="py-4 text-center text-[12px] text-txtHeading h-56 grid grid-cols-1 items-center justify-center"
                  data-testid="marker-visual-icon-empty"
                >
                  <p>
                    No icon matches &quot;{iconSearch.trim()}&quot;
                    <br />
                    Try another keyword or browse all icons.
                  </p>
                </div>
              )}
            </div>

            {/* Selected icon */}
            {selectedIcon ? (
              <div className="flex flex-col gap-1">
                <span className="text-[12px] font-semibold text-txtHeading">
                  Selected Icon
                </span>
                <div className="flex items-center gap-2">
                  <span className="flex size-9 items-center justify-center rounded-lg border border-gainsboro">
                    <Icon
                      name={selectedIcon}
                      width={20}
                      height={20}
                      className="text-txtBody"
                      title={selectedIcon}
                    />
                  </span>
                  <span className="text-xs text-txtHeading">
                    {getLegendIconLabel(selectedIcon)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <span className="text-[14px] font-semibold text-txtHeading">
                  Selected Icon
                </span>
                <div className="flex items-center gap-2">
                  <span className="flex size-9 items-center justify-center rounded-lg border border-gainsboro">
                    <DefaultImageIcon
                      className="size-5 text-txtBody"
                      title="default-image"
                    />
                  </span>
                  <span className="text-xs text-txtHeading">
                    No icon selected, please add icon above
                  </span>
                </div>
              </div>
            )}

            <Button
              type="button"
              size="sm"
              disabled={!selectedIcon}
              onClick={handleApplyIcon}
              className="h-8 rounded-lg text-xs"
              data-testid="marker-visual-apply-icon"
            >
              Apply Icon
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default MarkerVisualPanel;
